import {
  type NotificationData,
  // eslint-disable-next-line no-restricted-imports
  showNotification,
  // eslint-disable-next-line no-restricted-imports
  updateNotification
} from "@mantine/notifications";
import { useCallback, useRef } from "react";
import type { NonUndefined } from "utility-types";
import type { ApiError } from "../../api";
import { NotificationPresets } from "../mantinePresets";
import { extractMessage } from "./extractMessage";
import { getAdjustedNotificationOptions } from "./getAdjustedNotificationOptions";
import { defaultNotificationOptions } from "./notifications.constants";

interface ShowToastParams extends Omit<NotificationData, "message"> {
  type?: keyof typeof NotificationPresets;
  message:
    | NotificationData["message"]
    | ApiError
    | Error
    | string[]
    | string
    | unknown; // hopefully we can remove unknown in the future
}

type ShowToastFn = (
  notificationProps: ShowToastParams
) => NonUndefined<NotificationData["id"]>;

type ShowErrorToastFn = (
  error: ShowToastParams["message"]
) => NonUndefined<NotificationData["id"]>;

interface UpdateToastParams extends ShowToastParams {
  id: NonUndefined<NotificationData["id"]>;
}

function useToast() {
  const timesToastUpdatedRef = useRef<Map<string, number>>(new Map());

  const showToast: ShowToastFn = useCallback(
    ({
      type = "info",
      message,
      ...notificationProps
    }: ShowToastParams): NonUndefined<NotificationData["id"]> => {
      const extractedMessage = extractMessage(message);
      const adjustedNotificationOptions = getAdjustedNotificationOptions({
        extractedMessage,
        type
      });

      const id = showNotification({
        ...adjustedNotificationOptions,
        ...notificationProps,
        message: extractedMessage,
        onClose: (props) => {
          timesToastUpdatedRef.current.delete(id);
          notificationProps.onClose?.(props);
        }
      });

      timesToastUpdatedRef.current.set(id, 0);

      return id;
    },
    []
  );

  const showErrorToast: ShowErrorToastFn = useCallback(
    (error: ShowToastParams["message"]) => {
      return showToast({ type: "error", message: error });
    },
    [showToast]
  );

  /**
   * Calculates a unique autoClose duration (must not be the same as before so the timer actually resets).
   * Therefore we add the number of times the toast was updated to the default autoClose time.
   * The resulting autoClose times will differ only by milliseconds from the default.
   */
  function calculateUniqueAutoClose(
    id: string,
    manualAutoClose?: NotificationData["autoClose"]
  ): NonUndefined<NotificationData["autoClose"]> {
    const timesUpdated = timesToastUpdatedRef.current.get(id) ?? 0;
    if (typeof manualAutoClose === "boolean") {
      return manualAutoClose;
    }
    const baseAutoClose: number =
      manualAutoClose ?? Number(defaultNotificationOptions.autoClose);
    return timesUpdated ? timesUpdated + baseAutoClose : baseAutoClose;
  }

  const updateToast = useCallback(
    ({
      type = "info",
      id,
      autoClose,
      ...notificationProps
    }: UpdateToastParams): NonUndefined<NotificationData["id"]> => {
      const extractedMessage = extractMessage(notificationProps.message);
      const adjustedNotificationOptions = getAdjustedNotificationOptions({
        extractedMessage,
        isUpdate: true
      });
      const finalAutoClose =
        typeof adjustedNotificationOptions.autoClose === "number"
          ? calculateUniqueAutoClose(
              id,
              autoClose ?? NotificationPresets[type]?.autoClose
            )
          : adjustedNotificationOptions.autoClose; // stick with "false" if isBigMessage

      timesToastUpdatedRef.current.set(
        id,
        (timesToastUpdatedRef.current.get(id) ?? 0) + 1
      );

      updateNotification({
        ...adjustedNotificationOptions,
        ...notificationProps,
        id: id,
        message: extractedMessage,
        autoClose: finalAutoClose,
        onClose: (props) => {
          timesToastUpdatedRef.current.delete(id);
          notificationProps.onClose?.(props);
        }
      });
      return id;
    },
    []
  );

  return { showToast, showErrorToast, updateToast };
}

export { useToast, ShowToastFn, ShowToastParams, ShowErrorToastFn };
